import type { IDiscardQuestion } from '../hooks/useModalCloseGuard';

/** Правка, о потере которой спросить перед уходом с неё: карточка, редактор, форма настроек, документ. */
export interface IUnsavedEditSource {
    /** Есть несохранённые изменения; читается в момент ухода, а не при рендере. */
    hasUnsavedInput: () => boolean;
    /** Идёт сохранение или действие: уйти с правки нельзя — ответ пришёл бы уже не ей. */
    busy?: () => boolean;
    /** Свои тексты вопроса об этой правке; по умолчанию — «Закрыть без сохранения?». */
    question?: Partial<IDiscardQuestion>;
    /**
     * Правка, которая сохраняется сама (черновик): уход со страницы ждёт её решения — `true` уходить, `false`
     * остаться (спросить, если сохранить не вышло, — её забота). Пока правка в режиме правки, уход ждёт решения и без
     * несохранённого; закрытие вкладки браузера предупреждает только по `hasUnsavedInput` и `unsavedOnUnload`.
     */
    beforeLeave?: () => Promise<boolean>;
    /**
     * Закрытие вкладки браузера: правки, которые досылка при уходе (`pagehide` + keepalive) не сохранит — например,
     * черновик больше лимита keepalive. Читается в `beforeunload`; переходы внутри приложения на неё не смотрят.
     */
    unsavedOnUnload?: () => boolean;
}

/** Показать вопрос; `answer(true)` — «Не сохранять», `answer(false)` — «Вернуться». */
export type UnsavedEditAsk = (answer: (discard: boolean) => void, question?: Partial<IDiscardQuestion>) => void;

/**
 * Ответ на запрос ухода: `true` — действие выполнено сразу, `false` — не выполнено (правка занята сохранением),
 * промис — после ответа на вопрос, `true` — согласились не сохранять и действие выполнено.
 */
export type DiscardResult = boolean | Promise<boolean>;

interface IEntry {
    /** В режиме правки: только такие правки спрашивают и держат перехват ухода со страницы. */
    editing: boolean;
    /** Область, где правка зарегистрирована: вопрос о ней — в её теме и хосте порталов. */
    home: UnsavedEditArea;
}

let discarding = 0;
let unloadConfirmed = 0;

/**
 * Уход со страницы уже подтверждён вопросом приложения (переключение учётной записи): предупреждение браузера
 * «Покинуть сайт?» о правках областей не повторяется — иначе «Остаться» оставило бы страницу на чужом токене.
 * Результат — снять подтверждение (уход не состоялся).
 */
export const confirmPageUnload = (): (() => void) => {
    unloadConfirmed += 1;
    let released = false;
    return () => {
        if (released) return;
        released = true;
        unloadConfirmed -= 1;
    };
};

/** Уход со страницы подтверждён (`confirmPageUnload`). */
export const pageUnloadConfirmed = (): boolean => unloadConfirmed > 0;

/** Действие после «Не сохранять»: вложенные проверки той же правки не спрашивают второй раз. */
const runDiscarding = (action: () => void) => {
    discarding += 1;
    try {
        action();
    } finally {
        discarding -= 1;
    }
};

/** Решение правки-черновика; отказ решения не запирает пользователя на странице. */
const decideLeave = (source: IUnsavedEditSource): Promise<boolean> =>
    (source.beforeLeave?.() ?? Promise.resolve(true)).catch((error: unknown) => {
        console.error('Ошибка при подготовке ухода со страницы', error);
        return true;
    });

/**
 * Правки области страницы. Правка регистрируется в ближайшей области и во всех объемлющих: корень видит все
 * правки страницы (уход со страницы), вложенная область (окно со своим списком и карточкой) — только свои.
 */
export class UnsavedEditArea {
    private readonly entries = new Map<IUnsavedEditSource, IEntry>();
    private readonly listeners = new Set<() => void>();

    constructor(
        private readonly parent: UnsavedEditArea | null,
        private readonly ask: UnsavedEditAsk,
    ) {}

    /** Зарегистрировать правку; результат — снять. */
    register(source: IUnsavedEditSource, editing: boolean, home: UnsavedEditArea = this): () => void {
        this.entries.set(source, { editing, home });
        const unregisterInParent = this.parent?.register(source, editing, home);
        this.notify();
        return () => {
            unregisterInParent?.();
            this.entries.delete(source);
            this.notify();
        };
    }

    readonly subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    /** Хоть одна правка в режиме правки: уход со страницы может потребовать вопроса. */
    readonly isEditing = (): boolean => [...this.entries.values()].some((entry) => entry.editing);

    readonly isBusy = (): boolean => [...this.entries.keys()].some((source) => source.busy?.() ?? false);

    readonly hasUnsavedInput = (): boolean => discarding === 0 && this.unsavedEntry() !== undefined;

    /** Закрытие вкладки потеряет правки: несохранённые или те, что не уйдут досылкой (`unsavedOnUnload`). */
    readonly losesOnUnload = (): boolean =>
        this.hasUnsavedInput() ||
        (discarding === 0 &&
            [...this.entries].some(([source, entry]) => entry.editing && (source.unsavedOnUnload?.() ?? false)));

    /** Уход со страницы ждёт ответа: есть несохранённая правка или правка, которая решает сама (`beforeLeave`). */
    readonly blocksLeave = (): boolean => this.hasUnsavedInput() || (discarding === 0 && this.deciders().length > 0);

    /**
     * Действие уводит с правок области (другая строка, «Создать», вкладка): при несохранённой правке — сначала
     * вопрос, пока правка сохраняется — действие не выполняется.
     */
    readonly confirmDiscard = (action?: () => void): DiscardResult => {
        if (discarding === 0) {
            if (this.isBusy()) return false;
            const unsaved = this.unsavedEntry();
            if (unsaved) {
                const [source, entry] = unsaved;
                return new Promise((resolve) =>
                    entry.home.ask((discard) => {
                        if (discard && action) runDiscarding(action);
                        resolve(discard);
                    }, source.question),
                );
            }
        }
        action?.();
        return true;
    };

    /**
     * Уход со страницы — повторяет его сам перехват: правки, которые решают сами (`beforeLeave`), — по очереди, о
     * прочих несохранённых — вопрос. `answer(true)` — уходить.
     */
    readonly confirmLeave = (answer: (leave: boolean) => void): void => {
        const deciders = this.deciders();
        if (!deciders.length) {
            this.askAbout(this.unsavedEntry(), answer);
            return;
        }
        // первое решение — сразу, в момент перехода: черновик досохраняется без лишней задержки
        const decide = (index: number): Promise<boolean> =>
            index < deciders.length
                ? decideLeave(deciders[index]).then((allowed) => allowed && decide(index + 1))
                : Promise.resolve(true);
        void decide(0).then((allowed) => {
            const unsaved = allowed ? this.unsavedEntry(true) : undefined;
            if (unsaved) this.askAbout(unsaved, answer);
            else answer(allowed);
        });
    };

    private askAbout(unsaved: [IUnsavedEditSource, IEntry] | undefined, answer: (discard: boolean) => void) {
        (unsaved?.[1].home ?? this).ask(answer, unsaved?.[0].question);
    }

    /** Правки в режиме правки, которые решают об уходе сами. */
    private deciders(): IUnsavedEditSource[] {
        return [...this.entries].filter(([source, entry]) => entry.editing && source.beforeLeave).map(([s]) => s);
    }

    private unsavedEntry(skipDeciders = false): [IUnsavedEditSource, IEntry] | undefined {
        for (const [source, entry] of this.entries) {
            if (skipDeciders && source.beforeLeave) continue;
            if (entry.editing && source.hasUnsavedInput()) return [source, entry];
        }
        return undefined;
    }

    private notify() {
        this.listeners.forEach((listener) => listener());
    }
}
